import React, { useEffect, useRef, useState } from 'react';

/**
 * Selector de color de la tapa.
 *
 * Muestra solo el color elegido como una muestra grande; al abrir el desplegable
 * cada opcion aparece con su propio cuadro, igual que la muestra, para no tener
 * que adivinar a qué color corresponde cada nombre.
 */
export function ColorSelect({ label, palette, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = (value || '').toUpperCase();
  const chosen = palette.find(c => c.value.toUpperCase() === current);

  // Cierra al pulsar fuera, como los menus de la plataforma.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const Swatch = ({ color, size = 22 }) => (
    <span
      aria-hidden="true"
      style={{
        width: size, height: size, borderRadius: 5, flex: '0 0 auto',
        background: color, display: 'inline-block',
        border: '1px solid rgba(var(--ink-overlay-rgb),.25)',
      }}
    />
  );

  return (
    <div className="col-md-6" ref={ref}>
      <label className="form-label text-white-50">{label}</label>
      <div className="position-relative">
        <button
          type="button"
          className="btn w-100 d-flex align-items-center gap-2 bg-dark text-white border-secondary"
          style={{ height: 38, justifyContent: 'flex-start' }}
          onClick={() => setOpen(o => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <Swatch color={current || 'transparent'} size={22} />
          <span className="text-truncate">{chosen ? chosen.label : 'Sin color'}</span>
          <span className="ms-auto text-white-50 small">▾</span>
        </button>

        {open && (
          <div
            role="listbox"
            className="position-absolute mt-1 py-1 rounded overflow-auto"
            style={{
              background: 'var(--ink-surface-raised)', zIndex: 20,
              border: '1px solid rgba(var(--ink-overlay-rgb),.12)',
              boxShadow: '0 10px 30px rgba(0,0,0,.35)',
              maxHeight: 220,
              // left/top calculadas por JS: sin ancho fijo para no forzar scroll horizontal
              left: 0, right: 0,
            }}
          >
            {palette.map(c => {
              const isSelected = c.value.toUpperCase() === current;
              return (
                <button
                  key={c.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className="d-flex align-items-center gap-2 text-white w-100"
                  style={{
                    background: isSelected ? 'rgba(127,187,35,.14)' : 'transparent',
                    border: 'none',
                    borderRadius: 6,
                    padding: '8px 10px',
                    justifyContent: 'flex-start',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                  onClick={() => { onChange(c.value); setOpen(false); }}
                >
                  <Swatch color={c.value} />
                  <span className="text-truncate">{c.label}</span>
                  {isSelected && <span className="ms-auto text-success">✓</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}